import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Clock, Edit3, Trash2, FileText, CheckCircle, User, Eye, X, Calendar, Tag, Flag, Building2, ArrowRight } from 'lucide-react';
import { getActivities, deleteActivity, getBagian, getUsers } from '../../api';

const statusStyles = {
  'Pending': { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
  'InProgress': { bg: '#dbeafe', color: '#1e40af', label: 'In Progress' },
  'Completed': { bg: '#d1fae5', color: '#065f46', label: 'Completed' },
};

const priorityColors = {
  'Low': '#6b7280',
  'Medium': '#f59e0b',
  'High': '#ef4444',
  'Urgent': '#dc2626',
};

export default function ListActivity() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [bagianList, setBagianList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [filterBagian, setFilterBagian] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('');
  const [filterCreator, setFilterCreator] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [viewDetail, setViewDetail] = useState(null);

  const load = () => {
    setLoading(true);
    getActivities()
      .then(res => {
        setData(res.data || []);
        setFiltered(res.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    getBagian().then(res => setBagianList(res.data || [])).catch(() => {});
    getUsers().then(res => setUserList(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let result = [...data];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(d =>
        d.name?.toLowerCase().includes(q) ||
        d.description?.toLowerCase().includes(q)
      );
    }
    if (filterStatus) result = result.filter(d => d.status === filterStatus);
    if (filterCategory) result = result.filter(d => d.category === filterCategory);
    if (filterBagian) result = result.filter(d => d.bagian === filterBagian);
    if (filterAssignedTo) {
      const filterId = parseInt(filterAssignedTo, 10);
      result = result.filter(d => d.assigned_to === filterId);
    }
    if (filterCreator) {
      result = result.filter(d => d.user_name === filterCreator);
    }
    setFiltered(result);
  }, [search, filterStatus, filterCategory, filterBagian, filterAssignedTo, filterCreator, data]);

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus activity ini?')) return;
    setDeleteId(id);
    try {
      await deleteActivity(id);
      setData(prev => prev.filter(d => d.id !== id));
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal hapus');
    } finally {
      setDeleteId(null);
    }
  };

  // Get unique values for category filter (from data, since category is free-form)
  const categories = [...new Set(data.map(d => d.category).filter(Boolean))];

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FileText size={24} />
              List Activity
            </h2>
            <p className="page-desc">Daftar semua activity ({filtered.length} dari {data.length})</p>
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/activity/add')}
            style={{ background: '#059669', color: 'white', border: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={16} />
            <span>Add Activity</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Cari activity..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '9px 12px 9px 36px', borderRadius: 8,
              border: '1px solid #d1d5db', fontSize: '0.85rem', outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          style={filterSelectStyle}>
          <option value="">Semua Status</option>
          <option value="Pending">Pending</option>
          <option value="InProgress">In Progress</option>
          <option value="Completed">Completed</option>
        </select>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
          style={filterSelectStyle}>
          <option value="">Semua Kategori</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterBagian} onChange={(e) => setFilterBagian(e.target.value)}
          style={filterSelectStyle}>
          <option value="">Semua Bagian</option>
          {bagianList.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
        </select>
        <select value={filterAssignedTo} onChange={(e) => setFilterAssignedTo(e.target.value)}
          style={filterSelectStyle}>
          <option value="">Semua User</option>
          {userList.map(u => (
            <option key={u.id} value={u.id}>
              {u.full_name || u.username}
            </option>
          ))}
        </select>
        <select value={filterCreator} onChange={(e) => setFilterCreator(e.target.value)}
          style={filterSelectStyle}>
          <option value="">Semua Creator</option>
          {userList.map(u => {
            const name = u.full_name || u.username;
            return <option key={u.id} value={name}>{name}</option>;
          })}
        </select>
      </div>

      {loading ? (
        <div className="page-loading">Memuat data...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#f9fafb', borderRadius: 16, border: '1px dashed #d1d5db' }}>
          <Clock size={48} style={{ color: '#9ca3af', marginBottom: 12, opacity: 0.5 }} />
          <h3 style={{ color: '#374151', marginBottom: 6, fontSize: '1.1rem' }}>Belum Ada Activity</h3>
          <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Mulai dengan menambahkan activity baru.</p>
          <button className="btn btn-primary" onClick={() => navigate('/activity/add')}
            style={{ marginTop: 16, background: '#059669', color: 'white', border: 'none' }}>
            <Plus size={16} /> Tambah Activity
          </button>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Activity</th>
                <th>Status</th>
                <th>Category</th>
                <th>Bagian</th>
                <th>Priority</th>
                <th>Date</th>
                <th>Assigned To</th>
                <th>Tagged Users</th>
                <th style={{ width: 80 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => {
                const st = statusStyles[d.status] || statusStyles['Pending'];
                const tagged = d.tagged_users || [];
                const primaryTagged = tagged.filter(t => t.id !== d.assigned_to);
                const MAX_VISIBLE = 3;
                const visibleTags = primaryTagged.slice(0, MAX_VISIBLE);
                const extraCount = primaryTagged.length - MAX_VISIBLE;
                return (
                  <tr key={d.id}>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{d.name}</div>
                      {d.description && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {d.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '3px 10px', borderRadius: 20, fontSize: '0.72rem',
                        fontWeight: 600, background: st.bg, color: st.color
                      }}>
                        {d.status === 'InProgress' ? <Clock size={12} /> : d.status === 'Completed' ? <CheckCircle size={12} /> : <Clock size={12} />}
                        {st.label}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.82rem', color: '#374151' }}>{d.category || '-'}</td>
                    <td style={{ fontSize: '0.82rem' }}>{d.bagian || '-'}</td>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                        fontSize: '0.72rem', fontWeight: 700, color: 'white',
                        background: priorityColors[d.priority] || '#6b7280'
                      }}>
                        {d.priority || 'Medium'}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.82rem', color: '#6b7280' }}>{d.tanggal || '-'}</td>
                    <td>
                      {d.assigned_name ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{
                            width: 26, height: 26, borderRadius: '50%',
                            background: '#e0e7ff', color: '#4338ca',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.7rem', fontWeight: 700
                          }}>
                            {d.assigned_name.charAt(0).toUpperCase()}
                          </div>
                          <span style={{ fontSize: '0.82rem' }}>{d.assigned_name}</span>
                        </div>
                      ) : (
                        <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td>
                      {primaryTagged.length > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                          {visibleTags.map(t => (
                            <span
                              key={t.id}
                              title={`${t.name || t.username}${t.bagian ? ` (${t.bagian})` : ''}`}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                padding: '2px 8px', borderRadius: 12,
                                background: '#f0fdf4', border: '1px solid #d1fae5',
                                fontSize: '0.7rem', color: '#166534'
                              }}
                            >
                              <User size={10} />
                              {t.name || t.username}
                            </span>
                          ))}
                          {extraCount > 0 && (
                            <span style={{
                              fontSize: '0.7rem', color: '#9ca3af', fontWeight: 500,
                              padding: '2px 6px', borderRadius: 12,
                              background: '#f3f4f6'
                            }}>
                              +{extraCount} lainnya
                            </span>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm" title="Lihat Detail"
                          onClick={() => setViewDetail(d)}
                          style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', background: '#f0fdf4' }}>
                          <Eye size={13} style={{ color: '#059669' }} />
                        </button>
                        <button className="btn btn-sm" title="Edit"
                          onClick={() => navigate(`/activity/edit/${d.id}`)}
                          style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', background: 'white' }}>
                          <Edit3 size={13} style={{ color: '#6b7280' }} />
                        </button>
                        <button className="btn btn-sm" title="Hapus"
                          onClick={() => handleDelete(d.id)}
                          disabled={deleteId === d.id}
                          style={{ padding: '5px 8px', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', background: 'white' }}>
                          <Trash2 size={13} style={{ color: '#ef4444' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {viewDetail && (
        <DetailActivity data={viewDetail} onClose={() => setViewDetail(null)} />
      )}
    </div>
  );
}

// ====== Detail Activity Modal ======
function DetailActivity({ data, onClose }) {
  const d = data;
  const st = statusStyles[d.status] || statusStyles['Pending'];
  const tagged = d.tagged_users || [];

  const infoItems = [
    { label: 'Activity Name', value: d.name, icon: FileText },
    { label: 'Status', value: d.status, icon: CheckCircle, badge: { bg: st.bg, color: st.color, label: st.label } },
    { label: 'Category', value: d.category || '-', icon: Tag },
    { label: 'Priority', value: d.priority || '-', icon: Flag, chip: { color: priorityColors[d.priority] || '#6b7280' } },
    { label: 'Bagian / Dept', value: d.bagian || '-', icon: Building2 },
    { label: 'Date', value: d.tanggal || '-', icon: Calendar },
    { label: 'Penanggung Jawab', value: d.assigned_name || '-', icon: User },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}
      style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '5vh' }}>
      <div onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 16, width: '95%', maxWidth: 700,
          maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #059669, #047857)',
          color: 'white', padding: '20px 28px', borderRadius: '16px 16px 0 0',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 10,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '1px', opacity: 0.8, marginBottom: 4 }}>
              Detail Activity
            </div>
            <h3 style={{ color: 'white', margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>
              {d.name}
            </h3>
            {d.user_name && (
              <div style={{ fontSize: '0.8rem', opacity: 0.85, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <User size={12} />
                Dibuat oleh: {d.user_name}
              </div>
            )}
          </div>
          <button onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
              borderRadius: '50%', width: 32, height: 32, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, transition: 'all 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '24px 28px' }}>
          {/* Info Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            {infoItems.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '10px 14px', borderRadius: 10,
                  background: '#f9fafb', border: '1px solid #f3f4f6',
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: '#f0fdf4', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, color: '#059669',
                  }}>
                    <Icon size={16} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 2 }}>
                      {item.label}
                    </div>
                    {item.badge ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '3px 10px', borderRadius: 20, fontSize: '0.78rem',
                        fontWeight: 600, background: item.badge.bg, color: item.badge.color,
                      }}>
                        {item.badge.label}
                      </span>
                    ) : item.chip ? (
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                        fontSize: '0.75rem', fontWeight: 700, color: 'white',
                        background: item.chip.color,
                      }}>
                        {item.value}
                      </span>
                    ) : (
                      <span style={{ fontSize: '0.88rem', fontWeight: 500, color: '#374151', wordBreak: 'break-word' }}>
                        {item.value}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Description - Full Text */}
          {d.description ? (
            <div style={{
              background: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6',
              padding: '16px 20px', marginBottom: 20,
            }}>
              <div style={{
                fontSize: '0.7rem', fontWeight: 600, color: '#9ca3af',
                textTransform: 'uppercase', letterSpacing: '0.3px',
                marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <FileText size={13} />
                Description
              </div>
              <div style={{
                fontSize: '0.88rem', color: '#374151', lineHeight: 1.7,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {d.description}
              </div>
            </div>
          ) : (
            <div style={{
              background: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6',
              padding: '16px 20px', marginBottom: 20,
            }}>
              <div style={{
                fontSize: '0.7rem', fontWeight: 600, color: '#9ca3af',
                textTransform: 'uppercase', letterSpacing: '0.3px',
                marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <FileText size={13} />
                Description
              </div>
              <span style={{ fontSize: '0.85rem', color: '#9ca3af', fontStyle: 'italic' }}>
                Tidak ada description
              </span>
            </div>
          )}

          {/* Tagged Users */}
          {tagged.length > 0 && (
            <div style={{
              background: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6',
              padding: '16px 20px', marginBottom: 20,
            }}>
              <div style={{
                fontSize: '0.7rem', fontWeight: 600, color: '#9ca3af',
                textTransform: 'uppercase', letterSpacing: '0.3px',
                marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <User size={13} />
                Tagged Users ({tagged.length})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {tagged.map(t => {
                  const isPrimary = t.id === d.assigned_to;
                  return (
                    <div key={t.id} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 12px', borderRadius: 20,
                      background: isPrimary ? '#e0e7ff' : '#f0fdf4',
                      border: isPrimary ? '1px solid #c7d2fe' : '1px solid #bbf7d0',
                      fontSize: '0.82rem',
                      color: isPrimary ? '#4338ca' : '#166534',
                    }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%',
                        background: isPrimary ? '#4338ca' : '#059669',
                        color: 'white', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.6rem', fontWeight: 700,
                      }}>
                        {(t.name || t.username || '?').charAt(0).toUpperCase()}
                      </div>
                      <span>{t.name || t.username}</span>
                      {t.bagian && (
                        <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                          ({t.bagian})
                        </span>
                      )}
                      {isPrimary && (
                        <span style={{
                          fontSize: '0.62rem', fontWeight: 700, color: '#4338ca',
                          background: '#dbeafe', padding: '1px 6px', borderRadius: 4,
                        }}>
                          PIC
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Time Info */}
          {(d.start_time || d.end_time) && (
            <div style={{
              background: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6',
              padding: '16px 20px', marginBottom: 20,
            }}>
              <div style={{
                fontSize: '0.7rem', fontWeight: 600, color: '#9ca3af',
                textTransform: 'uppercase', letterSpacing: '0.3px',
                marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <Clock size={13} />
                Waktu
              </div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                {d.start_time && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#374151' }}>
                    <ArrowRight size={14} style={{ color: '#059669' }} />
                    <span>Mulai: <strong>{new Date(d.start_time).toLocaleString('id-ID')}</strong></span>
                  </div>
                )}
                {d.end_time && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#374151' }}>
                    <ArrowRight size={14} style={{ color: '#ef4444' }} />
                    <span>Selesai: <strong>{new Date(d.end_time).toLocaleString('id-ID')}</strong></span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div style={{
            borderTop: '1px solid #e5e7eb', paddingTop: 16,
            display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
            fontSize: '0.72rem', color: '#9ca3af',
          }}>
            {d.created_at && <span>Dibuat: {new Date(d.created_at).toLocaleString('id-ID')}</span>}
            {d.updated_at && <span>Diperbarui: {new Date(d.updated_at).toLocaleString('id-ID')}</span>}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 28px', borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'flex-end',
          background: '#f9fafb', borderRadius: '0 0 16px 16px',
        }}>
          <button onClick={onClose}
            style={{
              padding: '8px 24px', borderRadius: 8,
              background: '#059669', color: 'white', border: 'none',
              fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#047857'}
            onMouseLeave={e => e.currentTarget.style.background = '#059669'}>
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
}

const filterSelectStyle = {
  padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db',
  fontSize: '0.85rem', outline: 'none', background: 'white', minWidth: 140
};


